import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { m } from "framer-motion";
import { Link } from "react-router-dom";
import { useLocalePath } from "@/hooks/useLocalePath";
import { Users, Leaf, ShieldCheck, MapPin, Eye, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const pillarsData = [
  {
    id: 'human',
    icon: Users,
    iconColor: 'text-[#C45B3E]',
    badgeBg: 'bg-[#C45B3E]/10',
    bgColor: 'bg-[#C45B3E]',
    front: {
      fr: { title: 'Éthique', tagline: "L'humain avant le profit. Parce qu'aucun vêtement ne devrait coûter une vie." },
      en: { title: 'Ethics', tagline: "Human before profit. Because no garment should cost a life." }
    },
    back: {
      fr: { title: 'Ce qu\'on analyse vraiment', points: [{ bold: 'Droit du travail', text: 'Lois et sécurité des ouvriers.' }, { bold: 'Traçabilité', text: 'Liste publique des usines.' }, { bold: 'Labels sociaux', text: 'Audits certifiés (Fair Wear, SA8000).' }] },
      en: { title: 'What we really analyze', points: [{ bold: 'Labor rights', text: 'Laws and worker safety.' }, { bold: 'Traceability', text: 'Public list of factories.' }, { bold: 'Social labels', text: 'Certified audits (Fair Wear, SA8000).' }] }
    }
  },
  {
    id: 'earth',
    icon: Leaf,
    iconColor: 'text-[#6B8E7A]',
    badgeBg: 'bg-[#6B8E7A]/10',
    bgColor: 'bg-[#6B8E7A]',
    front: {
      fr: { title: 'Écologie', tagline: "Bilan réel. La planète peut-elle vraiment encaisser cette production ?" },
      en: { title: 'Ecology', tagline: "Real impact. Can the planet really handle this production?" }
    },
    back: {
      fr: { title: 'Ce qu\'on analyse vraiment', points: [{ bold: 'Matières', text: 'Fibres naturelles, bio ou recyclées.' }, { bold: 'Santé', text: 'Zéro produit chimique ou toxique.' }, { bold: 'Climat', text: 'Réduction carbone et économie d\'eau.' }] },
      en: { title: 'What we really analyze', points: [{ bold: 'Materials', text: 'Natural, organic or recycled fibers.' }, { bold: 'Health', text: 'Zero chemicals or toxins.' }, { bold: 'Climate', text: 'Carbon reduction and water savings.' }] }
    }
  },
  {
    id: 'time',
    icon: ShieldCheck,
    iconColor: 'text-[#2D5F5D]',
    badgeBg: 'bg-[#2D5F5D]/10',
    bgColor: 'bg-[#2D5F5D]',
    front: {
      fr: { title: 'Durabilité', tagline: "L'anti-jetable. Conçu pour durer ou programmé pour lâcher ?" },
      en: { title: 'Durability', tagline: "Anti-throwaway. Built to last or designed to fail?" }
    },
    back: {
      fr: { title: 'Ce qu\'on analyse vraiment', points: [{ bold: 'Surproduction', text: 'Limitation des volumes produits.' }, { bold: 'Qualité', text: 'Robustesse des matières et finitions.' }, { bold: 'Réparation', text: 'Services de seconde vie garantis.' }] },
      en: { title: 'What we really analyze', points: [{ bold: 'Overproduction', text: 'Limited production volumes.' }, { bold: 'Quality', text: 'Robust materials and finishes.' }, { bold: 'Repair', text: 'Guaranteed second-life services.' }] }
    }
  },
  {
    id: 'territory',
    icon: MapPin,
    iconColor: 'text-[#D4A574]',
    badgeBg: 'bg-[#F1CCAB]/40',
    bgColor: 'bg-[#D4A574]',
    front: {
      fr: { title: 'Proximité', tagline: "Impact local. Moins de kérosène, plus d'emplois chez nous." },
      en: { title: 'Proximity', tagline: "Local impact. Less kerosene, more jobs at home." }
    },
    back: {
      fr: { title: 'Ce qu\'on analyse vraiment', points: [{ bold: 'Trajet', text: 'Kilomètres réels usine-dressing.' }, { bold: 'Zéro Avion', text: 'Engagement contre le fret aérien.' }, { bold: 'Économie', text: 'Soutien aux emplois locaux.' }] },
      en: { title: 'What we really analyze', points: [{ bold: 'Journey', text: 'Actual kilometers from factory to wardrobe.' }, { bold: 'Zero Air Freight', text: 'Commitment against air shipping.' }, { bold: 'Economy', text: 'Support for local jobs.' }] }
    }
  },
  {
    id: 'vision',
    icon: Eye,
    iconColor: 'text-[#8E6B4B]',
    badgeBg: 'bg-[#8E6B4B]/15',
    bgColor: 'bg-[#8E6B4B]',
    front: {
      fr: { title: 'Transparence', tagline: "Zéro écran de fumée. Finis les impôts ailleurs et le greenwashing." },
      en: { title: 'Transparency', tagline: "Zero smokescreen. No more offshore taxes and greenwashing." }
    },
    back: {
      fr: { title: 'Ce qu\'on analyse vraiment', points: [{ bold: 'Fiscalité', text: 'Impôts payés dans le pays de vente.' }, { bold: 'Propriété', text: 'Clarté sur le capital et l\'indépendance.' }, { bold: 'Anti-Bullshit', text: 'Preuves réelles contre le greenwashing.' }] },
      en: { title: 'What we really analyze', points: [{ bold: 'Taxation', text: 'Taxes paid in the country of sale.' }, { bold: 'Ownership', text: 'Clarity on capital and independence.' }, { bold: 'Anti-Bullshit', text: 'Real proof against greenwashing.' }] }
    }
  }
];

const FlipCard = ({ pillar, language }: { pillar: typeof pillarsData[0]; language: 'fr' | 'en' }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const Icon = pillar.icon;
  const frontContent = pillar.front[language];
  const backContent = pillar.back[language];

  return (
    <div 
      className="perspective-1000 h-80 md:h-[22rem] cursor-pointer group"
      onClick={() => setIsFlipped(!isFlipped)}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <m.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="absolute inset-0 w-full h-full backface-hidden" style={{ backfaceVisibility: "hidden" }}>
          <div className="h-full bg-card border border-border rounded-2xl p-6 flex flex-col items-center justify-center text-center shadow-soft hover:shadow-medium transition-shadow duration-300">
            <m.div 
              className={`w-16 h-16 rounded-xl ${pillar.badgeBg} flex items-center justify-center mb-4`}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Icon className={`w-8 h-8 ${pillar.iconColor}`} />
            </m.div>
            <h3 className={`font-serif text-xl font-bold mb-3 ${pillar.iconColor}`}>{frontContent.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed font-medium">{frontContent.tagline}</p>
          </div>
        </div>

        <div className="absolute inset-0 w-full h-full backface-hidden" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
          <div className={`h-full ${pillar.bgColor} rounded-2xl p-4 md:p-5 flex flex-col justify-center text-white shadow-medium`}>
            <h4 className="font-serif text-base md:text-lg font-semibold mb-3 text-center text-white">{backContent.title}</h4>
            <ul className="space-y-2">
              {backContent.points.map((point, idx) => (
                <li key={idx} className="text-xs md:text-sm leading-relaxed">
                  <span className="font-bold text-white">{point.bold} : </span>
                  <span className="text-white/90">{point.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </m.div>
    </div>
  );
};

const InteractivePillars = () => {
  const { language, t } = useLanguage();
  const { getPath } = useLocalePath();

  const content = {
    fr: { title: 'Les 5 Axes du Wise Compass', subtitle: 'Notre méthodologie unique pour évaluer chaque marque', cta: 'Lequel est votre priorité ? Utilisez nos filtres pour personnaliser votre boussole.', button: 'Lancer ma recherche' },
    en: { title: 'The 5 Axes of the Wise Compass', subtitle: 'Our unique methodology for evaluating each brand', cta: 'Which is your priority? Use our filters to personalize your compass.', button: 'Start my search' }
  };

  const c = content[language];

  return (
    <section id="pillars" className="py-20 px-6 relative overflow-hidden bg-muted/40">
      <m.div 
        className="absolute top-20 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl"
        animate={{ x: [0, 30, 0], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <m.div 
        className="absolute bottom-10 left-0 w-60 h-60 bg-accent/8 rounded-full blur-3xl"
        animate={{ x: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto relative">
        <m.div 
          className="max-w-3xl mx-auto text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-serif text-3xl md:text-5xl text-foreground mb-4">{c.title}</h2>
          <p className="text-muted-foreground text-lg">{c.subtitle}</p>
        </m.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {pillarsData.map((pillar, index) => (
            <m.div
              key={pillar.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FlipCard pillar={pillar} language={language} />
            </m.div>
          ))}
        </div>

        <m.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p className="text-lg text-foreground mb-6 max-w-2xl mx-auto">{c.cta}</p>
          <Button asChild size="lg" className="group">
            <Link to={getPath("/explorer")} className="flex items-center gap-2">
              {c.button}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </m.div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-center mt-8"
        >
          <Link 
            to={getPath(language === "fr" ? "/blog/methodologie-indicateur-mode-ecoresponsable" : "/blog/wise-compass-score-methodology")}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium group"
          >
            {language === "fr" ? "Comprendre notre méthode d'analyse" : "Understand our analysis method"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </m.div>
      </div>
    </section>
  );
};

export default InteractivePillars;
